// ---------------------------------------------------------------------------
// Telephony onboarding — client-side format validation.
//
// Mirrors dialer_verification.md Appendix B + dialer_voice_integrity.md
// Appendix B. Bad values are otherwise rejected later during carrier review,
// so we catch them before submit. The backend re-validates authoritatively.
// ---------------------------------------------------------------------------

import { toE164 } from '../../../utils/phone'

// Every typed phone number is read by toE164; this is what a failed read says.
export const INVALID_PHONE =
  'We couldn’t read that number. Check the digits, and start with the country code if it’s outside the US or Canada (e.g. +44 20 7946 0958).'

export const EIN_RE = /^\d{2}-?\d{7}$/
export const DUNS_RE = /^\d{9}$/
export const US_ZIP_RE = /^\d{5}(-\d{4})?$/
export const CA_POSTAL_RE = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/
// PO-box detection for the street field (physical address only — no PO boxes).
export const PO_BOX_RE = /\bp\.?\s*o\.?\s*(box|b0x)\b/i
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function isHttpsUrl(value: string): boolean {
  const v = String(value || '').trim()
  if (!v.toLowerCase().startsWith('https://')) return false
  try {
    // eslint-disable-next-line no-new
    new URL(v)
    return true
  } catch {
    return false
  }
}

export function isEmail(value: string): boolean {
  return EMAIL_RE.test(String(value || '').trim())
}

export type VerificationForm = Record<string, string>
export type FieldErrors = Record<string, string>

function req(errors: FieldErrors, form: VerificationForm, key: string, label: string): void {
  if (!String(form[key] || '').trim()) errors[key] = `${label} is required.`
}

// Validate a single wizard step (1–4). Returns a map of formKey → message.
export function validateVerificationStep(step: number, form: VerificationForm): FieldErrors {
  const errors: FieldErrors = {}

  if (step === 1) {
    const name = String(form.businessName || '').trim()
    if (!name) errors.businessName = 'Business name is required.'
    else if (name.length > 64) errors.businessName = 'Keep the business name to 64 characters or fewer.'
  }

  if (step === 2) {
    req(errors, form, 'legalBusinessName', 'Legal business name')
    req(errors, form, 'businessIdentity', 'Business identity')
    req(errors, form, 'businessType', 'Business type')
    req(errors, form, 'industry', 'Industry')
    req(errors, form, 'registrationIdentifierType', 'Registration ID type')
    const regType = String(form.registrationIdentifierType || '').trim()
    const regNum = String(form.registrationNumber || '').trim()
    if (!regNum) {
      errors.registrationNumber = 'Registration number is required.'
    } else if (regType === 'EIN' && !EIN_RE.test(regNum)) {
      errors.registrationNumber = 'Enter a valid EIN — 9 digits, XX-XXXXXXX (e.g. 12-3456789).'
    } else if (regType === 'DUNS' && !DUNS_RE.test(regNum)) {
      errors.registrationNumber = 'Enter a valid DUNS number — 9 digits.'
    }
    req(errors, form, 'regionOfOperation', 'Region of operation')
    const website = String(form.websiteUrl || '').trim()
    if (!website) errors.websiteUrl = 'Website URL is required.'
    else if (!isHttpsUrl(website)) errors.websiteUrl = 'Enter a valid https:// URL.'
    const social = String(form.socialMediaProfileUrl || '').trim()
    if (social && !isHttpsUrl(social)) errors.socialMediaProfileUrl = 'Enter a valid https:// URL.'
  }

  if (step === 3) {
    const customerName = String(form.customerName || '').trim()
    if (!customerName) errors.customerName = 'Name on address is required.'
    else if (customerName.length > 16)
      errors.customerName = 'Keep this to 16 characters or fewer — don’t use the full legal name.'
    const street = String(form.street || '').trim()
    if (!street) errors.street = 'Street address is required.'
    else if (PO_BOX_RE.test(street)) errors.street = 'A physical address is required — PO boxes can’t be verified.'
    req(errors, form, 'city', 'City')
    req(errors, form, 'region', 'State / province')
    req(errors, form, 'isoCountry', 'Country')
    const country = String(form.isoCountry || '').trim()
    const postal = String(form.postalCode || '').trim()
    if (!postal) {
      errors.postalCode = 'Postal code is required.'
    } else if (country === 'US' && !US_ZIP_RE.test(postal)) {
      errors.postalCode = 'Enter a valid US ZIP code (NNNNN or NNNNN-NNNN).'
    } else if (country === 'CA' && !CA_POSTAL_RE.test(postal)) {
      errors.postalCode = 'Enter a valid Canadian postal code (A1A 1A1).'
    }
  }

  if (step === 4) {
    req(errors, form, 'firstName', 'First name')
    req(errors, form, 'lastName', 'Last name')
    const email = String(form.email || '').trim()
    if (!email) errors.email = 'Email is required.'
    else if (!isEmail(email)) errors.email = 'Enter a valid email address.'
    const phone = String(form.phoneNumber || '').trim()
    if (!phone) errors.phoneNumber = 'Phone number is required.'
    else if (!toE164(phone)) errors.phoneNumber = INVALID_PHONE
    req(errors, form, 'businessTitle', 'Business title')
    req(errors, form, 'jobPosition', 'Job position')
  }

  return errors
}

export interface VerificationValidation {
  valid: boolean
  byStep: Record<number, FieldErrors>
  firstInvalidStep: number | null
}

// Validate all data steps (1–4) at once (used before final submit).
export function validateVerificationAll(form: VerificationForm): VerificationValidation {
  const byStep: Record<number, FieldErrors> = {}
  let firstInvalidStep: number | null = null
  for (const step of [1, 2, 3, 4]) {
    const errs = validateVerificationStep(step, form)
    byStep[step] = errs
    if (firstInvalidStep === null && Object.keys(errs).length > 0) firstInvalidStep = step
  }
  return { valid: firstInvalidStep === null, byStep, firstInvalidStep }
}

// ── Voice integrity ────────────────────────────────────────────────────────

export interface VoiceIntegrityForm {
  useCase: string
  businessEmployeeCount: string | number
  averageBusinessDayCallVolume: string | number
  notes: string
}

function isPositiveInt(v: string | number): boolean {
  const n = typeof v === 'number' ? v : Number(String(v).trim())
  return Number.isInteger(n) && n >= 1
}

export function validateVoiceIntegrity(form: VoiceIntegrityForm): FieldErrors {
  const errors: FieldErrors = {}
  if (!String(form.useCase || '').trim()) errors.useCase = 'Pick what your calls are mostly for.'
  if (!isPositiveInt(form.businessEmployeeCount))
    errors.businessEmployeeCount = 'Enter a whole number of employees (at least 1).'
  if (!isPositiveInt(form.averageBusinessDayCallVolume))
    errors.averageBusinessDayCallVolume = 'Enter a whole number of calls per day (at least 1).'
  // notes optional
  return errors
}
